export type TimeScale = 'day' | 'week' | 'month' | 'fortnight';
export interface TimeAxis {
  boundaries: number[];
  stepWidth: number;
  start: string;
  end: string;
}
export interface TimeShift {
  id: string;
  rowId: string;
  title: string;
  start: string;
  end: string;
  tone?: 'info' | 'success' | 'warning';
  tentative?: boolean;
}

function checkedDay(day: string): number {
  const value = Date.parse(day + 'T00:00:00Z');
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(day) ||
    !Number.isFinite(value) ||
    new Date(value).toISOString().slice(0, 10) !== day
  )
    throw Error('Некорректная календарная дата');
  return value;
}
export function addCalendarDays(day: string, days: number): string {
  return new Date(checkedDay(day) + days * 86400000).toISOString().slice(0, 10);
}
function monthStart(day: string, months: number): string {
  const date = new Date(checkedDay(day));
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1)).toISOString().slice(0, 10);
}

/** Полночь в поясе, а не фиксированные 24 часа: смена пояса и DST сохраняют календарь. */
export function zonedMidnight(day: string, timeZone: string): number {
  return zonedHour(day, 0, timeZone);
}
/** Календарный час учитывает смену UTC-смещения, включая полдень в день DST. */
export function zonedHour(day: string, hour: number, timeZone: string): number {
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) throw Error('Некорректный час');
  const target = checkedDay(day) + hour * 3600000;
  const format = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  let guess = target;
  for (let i = 0; i < 4; i++) {
    const p = Object.fromEntries(format.formatToParts(guess).map((part) => [part.type, part.value]));
    const local = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    const delta = target - local;
    if (!delta) return guess;
    guess += delta;
  }
  throw Error('В этом поясе календарная полночь недоступна');
}
export function buildTimeAxis(
  scale: TimeScale,
  anchor: string,
  periods: number,
  timeZone = 'Europe/Moscow',
): TimeAxis {
  if (!Number.isInteger(periods) || periods < 1 || periods > 24) throw Error('Некорректное число периодов');
  checkedDay(anchor);
  let start = anchor;
  let end: string;
  if (scale === 'month') {
    start = monthStart(anchor, 0);
    end = monthStart(start, periods);
  } else if (scale === 'week' || scale === 'fortnight') {
    const weekday = new Date(checkedDay(anchor)).getUTCDay();
    start = addCalendarDays(anchor, -(weekday + 6) % 7);
    end = addCalendarDays(start, (scale === 'fortnight' ? 14 : 7) * periods);
  } else end = addCalendarDays(start, periods);
  const from = zonedMidnight(start, timeZone);
  const to = zonedMidnight(end, timeZone);
  const boundaries: number[] = [];
  if (scale === 'day') {
    for (let t = from; t < to; t += 3600000) boundaries.push(t);
  } else {
    for (let day = start; day < end; day = addCalendarDays(day, 1)) {
      boundaries.push(zonedMidnight(day, timeZone));
      if (scale === 'fortnight') boundaries.push(zonedHour(day, 12, timeZone));
    }
  }
  boundaries.push(to);
  return { boundaries, stepWidth: scale === 'day' ? 64 : scale === 'week' ? 160 : 48, start, end };
}

export function timePixel(time: number, axis: TimeAxis): number {
  const b = axis.boundaries;
  if (!Number.isFinite(time)) throw Error('Некорректный момент времени');
  let i = 0;
  while (i < b.length - 2 && time >= b[i + 1]) i++;
  return (i + (time - b[i]) / (b[i + 1] - b[i])) * axis.stepWidth;
}
export function packShifts(shifts: TimeShift[]): { shift: TimeShift; lane: number }[] {
  const lanes: number[] = [];
  return shifts
    .filter((s) => Number.isFinite(Date.parse(s.start)) && Date.parse(s.end) > Date.parse(s.start))
    .sort((a, b) => Date.parse(a.start) - Date.parse(b.start) || Date.parse(a.end) - Date.parse(b.end))
    .map((shift) => {
      const start = Date.parse(shift.start),
        end = Date.parse(shift.end);
      let lane = lanes.findIndex((last) => last <= start);
      if (lane < 0) lane = lanes.length;
      lanes[lane] = end;
      return { shift, lane };
    });
}
